import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import {
  createLead,
  deleteLead,
  getAgentLeadSummary,
  getLeadById,
  listLeads,
  updateLead
} from '../services/leadsService'

const DEFAULT_STALE_TIME = 1000 * 60 * 2
const DEFAULT_CACHE_TIME = 1000 * 60 * 10

export const leadsKeys = {
  all: ['leads'],
  lists: () => [...leadsKeys.all, 'list'],
  list: (options = {}) => [...leadsKeys.lists(), options],
  details: () => [...leadsKeys.all, 'detail'],
  detail: (leadId, options = {}) => [...leadsKeys.details(), leadId, options],
  summary: (options = {}) => [...leadsKeys.all, 'summary', options]
}

const upsertLeadInCache = (queryClient, lead) => {
  const detailQueries = queryClient.getQueriesData({
    queryKey: leadsKeys.details()
  })

  detailQueries.forEach(([queryKey, data]) => {
    if (data && data.id !== lead.id) return
    queryClient.setQueryData(queryKey, lead)
  })

  const listQueries = queryClient.getQueriesData({
    queryKey: leadsKeys.lists()
  })

  listQueries.forEach(([queryKey, data]) => {
    if (!Array.isArray(data)) return
    const existingIndex = data.findIndex((item) => item.id === lead.id)
    if (existingIndex === -1) {
      queryClient.setQueryData(queryKey, [lead, ...data])
      return
    }

    const next = [...data]
    next[existingIndex] = lead
    queryClient.setQueryData(queryKey, next)
  })

  queryClient.invalidateQueries({ queryKey: leadsKeys.summary() })
}

const subscribeToLeads = (queryClient, leadId) => {
  const channel = supabase.channel('leads-realtime')
  const handleRealtimePayload = (payload) => {
    if (!payload?.new) return
    if (leadId && payload.new.id !== leadId) return
    upsertLeadInCache(queryClient, payload.new)
  }

  channel
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'leads' },
      handleRealtimePayload
    )
    .on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'leads' },
      handleRealtimePayload
    )
    .subscribe()

  return () => {
    supabase.removeChannel(channel)
  }
}

export const useLeads = (options = {}, config = {}) => {
  const {
    enabled = true,
    realtime = false,
    staleTime = DEFAULT_STALE_TIME,
    cacheTime = DEFAULT_CACHE_TIME
  } = config
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!realtime) return undefined
    return subscribeToLeads(queryClient)
  }, [queryClient, realtime])

  return useQuery({
    queryKey: leadsKeys.list(options),
    queryFn: () => listLeads(options),
    enabled,
    staleTime,
    cacheTime
  })
}

export const useLead = (leadId, options = {}, config = {}) => {
  const {
    enabled = true,
    realtime = false,
    staleTime = DEFAULT_STALE_TIME,
    cacheTime = DEFAULT_CACHE_TIME
  } = config
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!realtime || !leadId) return undefined
    return subscribeToLeads(queryClient, leadId)
  }, [leadId, queryClient, realtime])

  return useQuery({
    queryKey: leadsKeys.detail(leadId, options),
    queryFn: () => getLeadById(leadId, options),
    enabled: enabled && Boolean(leadId),
    staleTime,
    cacheTime
  })
}

export const useLeadSummary = (options = {}, config = {}) => {
  const {
    enabled = true,
    staleTime = DEFAULT_STALE_TIME,
    cacheTime = DEFAULT_CACHE_TIME
  } = config

  return useQuery({
    queryKey: leadsKeys.summary(options),
    queryFn: () => getAgentLeadSummary(options),
    enabled,
    staleTime,
    cacheTime
  })
}

export const useCreateLead = (options = {}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload) => createLead(payload, options),
    onSuccess: (data) => {
      queryClient.setQueryData(leadsKeys.detail(data.id, options), data)
      queryClient.invalidateQueries({ queryKey: leadsKeys.lists() })
      queryClient.invalidateQueries({ queryKey: leadsKeys.summary() })
    }
  })
}

export const useUpdateLead = (options = {}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ leadId, updates }) => updateLead(leadId, updates, options),
    onMutate: async ({ leadId, updates }) => {
      await queryClient.cancelQueries({ queryKey: leadsKeys.all })

      const previousLists = queryClient.getQueriesData({
        queryKey: leadsKeys.lists()
      })
      const previousDetails = queryClient.getQueriesData({
        queryKey: leadsKeys.details()
      })

      previousLists.forEach(([queryKey, data]) => {
        if (!Array.isArray(data)) return
        queryClient.setQueryData(
          queryKey,
          data.map((item) =>
            item.id === leadId ? { ...item, ...updates } : item
          )
        )
      })

      previousDetails.forEach(([queryKey, data]) => {
        if (!data || data.id !== leadId) return
        queryClient.setQueryData(queryKey, { ...data, ...updates })
      })

      return { previousLists, previousDetails }
    },
    onError: (_error, _variables, context) => {
      if (!context) return
      context.previousLists?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })
      context.previousDetails?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: leadsKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: leadsKeys.detail(variables.leadId)
      })
      queryClient.invalidateQueries({ queryKey: leadsKeys.summary() })
    }
  })
}

export const useDeleteLead = (options = {}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (leadId) => deleteLead(leadId, options),
    onMutate: async (leadId) => {
      await queryClient.cancelQueries({ queryKey: leadsKeys.all })

      const previousLists = queryClient.getQueriesData({
        queryKey: leadsKeys.lists()
      })
      const previousDetails = queryClient.getQueriesData({
        queryKey: leadsKeys.details()
      })

      previousLists.forEach(([queryKey, data]) => {
        if (!Array.isArray(data)) return
        queryClient.setQueryData(
          queryKey,
          data.filter((item) => item.id !== leadId)
        )
      })

      previousDetails.forEach(([queryKey, data]) => {
        if (!data || data.id !== leadId) return
        queryClient.removeQueries({ queryKey, exact: true })
      })

      return { previousLists, previousDetails }
    },
    onError: (_error, _leadId, context) => {
      if (!context) return
      context.previousLists?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })
      context.previousDetails?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })
    },
    onSettled: (_data, _error, leadId) => {
      queryClient.invalidateQueries({ queryKey: leadsKeys.lists() })
      queryClient.invalidateQueries({ queryKey: leadsKeys.detail(leadId) })
      queryClient.invalidateQueries({ queryKey: leadsKeys.summary() })
    }
  })
}
